import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useRef,
  useState,
} from "react";

import { Corner, DragState, OverlayDimensions, SnapGuides } from "../types";
import {
  applyCenterSnap,
  clampPositionWithGutter,
  computeGeometry,
  computeResize,
  getCornerAnchor,
} from "../utils";

interface UsePlacementInteractionInput {
  containerRef: RefObject<HTMLElement | null>;
  overlayDims: OverlayDimensions | null;
  positionXPercent: number;
  positionYPercent: number;
  sizePercent: number;
  minSizePercent: number;
  maxSizePercent: number;
  onPositionChange: (xPercent: number, yPercent: number) => void;
  onSizeChange: (sizePercent: number) => void;
  onInteractionStart?: () => void;
}

interface UsePlacementInteractionResult {
  activeMode: DragState["mode"] | null;
  guides: SnapGuides;
  startInteraction: (
    mode: DragState["mode"],
    e: ReactPointerEvent<HTMLElement>,
  ) => void;
  handleMove: (e: ReactPointerEvent<HTMLElement>) => void;
  endInteraction: (e: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Инкапсулирует drag-state и pointer-обработчики для перемещения и
 * resize'а overlay'а на подложке. Возвращает текущий activeMode (для
 * визуальных стилей), snap-гайды и три обработчика для pointer events.
 */
export const usePlacementInteraction = ({
  containerRef,
  overlayDims,
  positionXPercent,
  positionYPercent,
  sizePercent,
  minSizePercent,
  maxSizePercent,
  onPositionChange,
  onSizeChange,
  onInteractionStart,
}: UsePlacementInteractionInput): UsePlacementInteractionResult => {
  const dragRef = useRef<DragState | null>(null);
  const [activeMode, setActiveMode] = useState<DragState["mode"] | null>(null);
  const [guides, setGuides] = useState<SnapGuides>({ h: false, v: false });

  const startInteraction = useCallback(
    (mode: DragState["mode"], e: ReactPointerEvent<HTMLElement>) => {
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);

      onInteractionStart?.();
      setActiveMode(mode);

      const geom = computeGeometry({
        container,
        overlayDims,
        sizePercent,
        positionXPercent,
        positionYPercent,
      });
      const state: DragState = {
        mode,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPosX: positionXPercent,
        startPosY: positionYPercent,
        startSize: sizePercent,
        containerRect: container.getBoundingClientRect(),
      };
      if (mode !== "move" && geom && overlayDims) {
        state.anchorPx = getCornerAnchor(mode as Corner, geom);
        state.overlayRatio = overlayDims.w / overlayDims.h;
      }
      dragRef.current = state;
    },
    [
      containerRef,
      onInteractionStart,
      overlayDims,
      positionXPercent,
      positionYPercent,
      sizePercent,
    ],
  );

  const handleMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const state = dragRef.current;
      if (!state) return;
      const rect = state.containerRect;
      const dx = e.clientX - state.startClientX;
      const dy = e.clientY - state.startClientY;

      if (state.mode === "move") {
        const geom = computeGeometry({
          container: containerRef.current,
          overlayDims,
          sizePercent,
          positionXPercent,
          positionYPercent,
        });
        const widthPct = geom ? (geom.displayW / rect.width) * 100 : 0;
        const heightPct = geom ? (geom.displayH / rect.height) * 100 : 0;

        let newX = state.startPosX + (dx / rect.width) * 100;
        let newY = state.startPosY + (dy / rect.height) * 100;

        if (geom) {
          const snapped = applyCenterSnap({ newX, newY, widthPct, heightPct });
          newX = snapped.x;
          newY = snapped.y;
          setGuides((prev) =>
            prev.v === snapped.guides.v && prev.h === snapped.guides.h
              ? prev
              : snapped.guides,
          );
        }

        const clamped = clampPositionWithGutter({
          x: newX,
          y: newY,
          widthPct,
          heightPct,
          rectWidth: rect.width,
          rectHeight: rect.height,
        });
        onPositionChange(clamped.x, clamped.y);
        return;
      }

      // resize-режим.
      if (!state.anchorPx || !state.overlayRatio || !overlayDims) return;
      const resized = computeResize({
        pointerXInContainer: e.clientX - rect.left,
        pointerYInContainer: e.clientY - rect.top,
        anchorPx: state.anchorPx,
        overlayRatio: state.overlayRatio,
        overlayDims,
        rectWidth: rect.width,
        rectHeight: rect.height,
        minSizePercent,
        maxSizePercent,
        corner: state.mode,
      });

      const widthPct = (resized.widthPx / rect.width) * 100;
      const heightPct = (resized.heightPx / rect.height) * 100;
      const clamped = clampPositionWithGutter({
        x: (resized.leftPx / rect.width) * 100,
        y: (resized.topPx / rect.height) * 100,
        widthPct,
        heightPct,
        rectWidth: rect.width,
        rectHeight: rect.height,
      });

      onSizeChange(resized.sizePercent);
      onPositionChange(clamped.x, clamped.y);
    },
    [
      containerRef,
      onPositionChange,
      onSizeChange,
      overlayDims,
      minSizePercent,
      maxSizePercent,
      positionXPercent,
      positionYPercent,
      sizePercent,
    ],
  );

  const endInteraction = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer уже отпущен — нормальный случай при pointercancel */
    }
    dragRef.current = null;
    setActiveMode(null);
    setGuides({ h: false, v: false });
  }, []);

  return { activeMode, guides, startInteraction, handleMove, endInteraction };
};
